import { useEffect, useRef, useState } from "react";
import { Flex } from "antd";
import { colors } from "shared/theme/color";
import { translate } from "shared/languages";
import { ReferralTrendPoint, TrendGranularity } from "shared/types";
import { localTzDate } from "shared/utils/dayjs";
import {
  VBOX_W,
  VBOX_H,
  PAD_LEFT,
  PAD_RIGHT,
  PAD_TOP,
  PLOT_H,
  countTicks,
  bandWidth,
  bandXFor,
  yFor,
} from "./chartUtils";
import { textPx } from "shared/constants/zoom";

interface ReferralTrendChartProps {
  points: ReferralTrendPoint[];
  granularity: TrendGranularity;
  // Rendered height, which also sets the figure's scale (height / VBOX_H).
  // Width follows the container.
  height?: number;
}

// Sizes its viewBox to the container instead of scaling a fixed 520-unit box
// to fit. The fixed box is height-capped by preserveAspectRatio="meet", so in
// any container wider than its aspect ratio it just floats in a letterbox.
// See StampDistributionChart for the full reasoning.
const MIN_VBOX_W = VBOX_W;

const TIP_W = 150;
const TIP_H = 48;

// Axis labels follow the server's granularity rather than re-deriving it, so
// the label can never disagree with the bucketing that produced the data.
const tickLabel = (bucketStart: string, granularity: TrendGranularity) => {
  const d = localTzDate(bucketStart);
  if (granularity === "month") return d.format("MMM");
  return d.format("D MMM");
};

const fullLabel = (bucketStart: string, granularity: TrendGranularity) => {
  const d = localTzDate(bucketStart);
  if (granularity === "month") return d.format("MMM YYYY");
  if (granularity === "week") {
    return `${d.format("D MMM")} – ${d.add(6, "day").format("D MMM YYYY")}`;
  }
  return d.format("D MMM YYYY");
};

// New referred clients per period. Flat navy, not the distribution chart's
// ramp: time carries no "closer to a goal" meaning, so a gradient here would
// encode nothing.
const ReferralTrendChart = ({
  points,
  granularity,
  height = VBOX_H,
}: ReferralTrendChartProps) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [vboxW, setVboxW] = useState(MIN_VBOX_W);

  // Solved for the figure's own scale so authored font sizes and strokes
  // render exactly as before; only the bands stretch. offsetWidth (layout px),
  // never a rect (visual px).
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const scale = height / VBOX_H;
    const measure = () =>
      setVboxW(Math.max(MIN_VBOX_W, Math.round(el.offsetWidth / scale)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [height]);

  const isEmpty = points.every((p) => p.conversions === 0);
  // Integer ticks — same reason as the stamp chart: these are clients.
  const { top: max, ticks } = countTicks(Math.max(...points.map((p) => p.conversions), 1));
  const baselineY = PAD_TOP + PLOT_H;
  const n = points.length;
  const plotW = vboxW - PAD_LEFT - PAD_RIGHT;
  const band = bandWidth(n, plotW);
  const barWidth = Math.min(24, Math.max(6, band * 0.5));

  // Thin out tick labels once the buckets outnumber the room for them (a
  // 53-week year on a card-width chart). Counted BACK FROM THE END so the most
  // recent bucket is always labelled: thinning from the start left the newest
  // bar — usually the only one anyone is looking at — sitting past the last
  // label with nothing under it.
  const MAX_TICKS = 10;
  const tickEvery = Math.max(1, Math.ceil(n / MAX_TICKS));
  const isTick = (i: number) => (n - 1 - i) % tickEvery === 0;

  const hovered = hoverIndex !== null ? points[hoverIndex] : null;
  const hoveredX = hoverIndex !== null ? bandXFor(hoverIndex, n, plotW) : 0;
  const tipLeft = hovered
    ? Math.min(Math.max(hoveredX - TIP_W / 2, PAD_LEFT), vboxW - PAD_RIGHT - TIP_W)
    : 0;
  const tipTop = hovered
    ? Math.max(yFor(hovered.conversions, max) - TIP_H - 10, PAD_TOP)
    : 0;

  return (
    <div ref={boxRef}>
      {isEmpty ? (
        <Flex
          align="center"
          justify="center"
          style={{ height, color: colors.ink3, fontSize: textPx(13) }}
        >
          {translate("analyticsPage.referral.trend_empty")}
        </Flex>
      ) : (
        <svg
          width="100%"
          height={height}
          viewBox={`0 0 ${vboxW} ${VBOX_H}`}
          preserveAspectRatio="xMidYMid meet"
        >
          {ticks.map((v) => {
            const y = yFor(v, max);
            return (
              <g key={v}>
                <line
                  x1={PAD_LEFT}
                  x2={vboxW - 8}
                  y1={y}
                  y2={y}
                  stroke={colors.line}
                  strokeWidth={1}
                />
                <text
                  x={PAD_LEFT - 8}
                  y={y + 3}
                  textAnchor="end"
                  fontSize={10}
                  fill={colors.ink3}
                >
                  {v}
                </text>
              </g>
            );
          })}

          {points.map((p, i) => {
            const x = bandXFor(i, n, plotW);
            const topY = yFor(p.conversions, max);
            const h = Math.max(0, baselineY - topY);
            const active = hoverIndex === i;
            const r = Math.min(4, barWidth / 2, h);
            return (
              <g key={p.bucket_start}>
                <path
                  d={`M ${x - barWidth / 2} ${baselineY}
                      L ${x - barWidth / 2} ${topY + r}
                      Q ${x - barWidth / 2} ${topY} ${x - barWidth / 2 + r} ${topY}
                      L ${x + barWidth / 2 - r} ${topY}
                      Q ${x + barWidth / 2} ${topY} ${x + barWidth / 2} ${topY + r}
                      L ${x + barWidth / 2} ${baselineY} Z`}
                  fill={colors.navy}
                  opacity={hoverIndex === null || active ? 1 : 0.45}
                />
                {isTick(i) && (
                  <text
                    x={x}
                    y={VBOX_H - 8}
                    textAnchor="middle"
                    fontSize={10}
                    fill={colors.ink3}
                  >
                    {tickLabel(p.bucket_start, granularity)}
                  </text>
                )}
                <rect
                  x={x - band / 2}
                  y={PAD_TOP}
                  width={band}
                  height={PLOT_H}
                  fill="transparent"
                  onMouseEnter={() => setHoverIndex(i)}
                  onMouseLeave={() => setHoverIndex(null)}
                />
              </g>
            );
          })}

          {hovered && (
            <g pointerEvents="none">
              <rect
                x={tipLeft}
                y={tipTop}
                width={TIP_W}
                height={TIP_H}
                rx={6}
                fill={colors.ink}
              />
              <text
                x={tipLeft + 12}
                y={tipTop + 20}
                fontSize={12}
                fontWeight={600}
                fill={colors.card}
              >
                {fullLabel(hovered.bucket_start, granularity)}
              </text>
              <text x={tipLeft + 12} y={tipTop + 38} fontSize={12} fill={colors.card}>
                {`${hovered.conversions} ${translate("analyticsPage.referral.client_unit")}`}
              </text>
            </g>
          )}
        </svg>
      )}
    </div>
  );
};

export default ReferralTrendChart;
